import React from 'react';
import { LayoutKey, SlideViewProps } from './types';
import { SlideContext, SlideCtx } from './parts/context';
import { themeVars, useDeckFont } from './theme';
import { CoverLayout } from './layouts/Cover';
import { StatementLayout } from './layouts/Statement';
import { BulletsLayout } from './layouts/Bullets';
import { StepsLayout } from './layouts/Steps';
import { MaterialsLayout } from './layouts/Materials';
import { DesignsLayout } from './layouts/Designs';
import { ImageLayout } from './layouts/ImageSlide';
import { QuoteLayout } from './layouts/Quote';
import { StatsLayout } from './layouts/Stats';
import { PricingLayout } from './layouts/Pricing';
import { TermsLayout } from './layouts/Terms';
import { TeamLayout } from './layouts/Team';
import { CtaLayout } from './layouts/Cta';
import { TableLayout } from './layouts/Table';

/** Design size: every slide is laid out at exactly this many CSS px, then scaled. */
export const SLIDE_WIDTH = 1920;
export const SLIDE_HEIGHT = 1080;

export const LAYOUTS: Record<LayoutKey, React.FunctionComponent> = {
    cover: CoverLayout,
    statement: StatementLayout,
    bullets: BulletsLayout,
    steps: StepsLayout,
    materials: MaterialsLayout,
    designs: DesignsLayout,
    image: ImageLayout,
    quote: QuoteLayout,
    stats: StatsLayout,
    pricing: PricingLayout,
    terms: TermsLayout,
    team: TeamLayout,
    cta: CtaLayout,
    table: TableLayout
};

// #region SlideView
/**
 * One slide at 1920×1080, themed entirely from context.theme. Pure: no API calls.
 * Wrap it in ScaledSlide to fit a container; print mode renders it unscaled.
 */
export const SlideView: React.FunctionComponent<SlideViewProps> = ({ slide, context, mode, selected, onFieldChange, onSelectField, renderImageSlot, onAction }): React.ReactElement => {
    useDeckFont(context.theme.font_css_url);
    const edit = mode === 'edit';
    const sig = React.useMemo(() => `${mode}:${JSON.stringify(slide.fields)}:${JSON.stringify(context.computed[slide.id] ?? null)}`, [mode, slide.fields, context.computed, slide.id]);
    const value = React.useMemo<SlideCtx>(() => ({
        slide, context, computed: context.computed[slide.id] ?? {}, mode, edit, sig, onFieldChange, onSelectField, renderImageSlot, onAction
    }), [slide, context, mode, edit, sig, onFieldChange, onSelectField, renderImageSlot, onAction]);
    const style = React.useMemo(() => themeVars(context.theme), [context.theme]);
    const Layout = LAYOUTS[slide.layout] ?? UnknownLayout;
    const cls = ['wd-slide', `wd-layout-${slide.layout}`, `wd-mode-${mode}`, edit && selected ? 'wd-selected' : ''].filter(Boolean).join(' ');
    return (
        <SlideContext.Provider value={value}>
            <div className={cls} style={style} data-slide-id={slide.id} onClick={edit ? () => onSelectField?.(null) : undefined}>
                <Layout />
            </div>
        </SlideContext.Provider>
    );
};

const UnknownLayout: React.FunctionComponent = (): React.ReactElement => (
    <div className="wd-pad" style={{ alignItems: 'center', justifyContent: 'center' }}><p className="wd-muted">This slide uses a layout this version can’t show.</p></div>
);
